import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  doc as firebaseDoc,
  runTransaction,
  getFirestore,
  collection,
  getDocs,
} from "firebase/firestore";

export const checkAvailability = createAsyncThunk(
  "carAvailability/checkAvailability",
  async (_, { getState }) => {
    const { date, session, people } = getState().carAvailability;
    const db = getFirestore();
    const docId = `${date}_${session}`;

    const docRef = firebaseDoc(db, "carAvailability", docId);

    // Get the list of active cars from the database
    const activeCars = await getActiveCarsFromDatabase();

    //get the document

    // We're using transaction to read and write the document
    //this ensures that if two users try to book at the same time,
    // one of the bookings will be retried and will get the updated data

    return runTransaction(db, async (transaction) => {
      if (session === "full day") {
        const morningDocId = `${date}_morning`;
        const afternoonDocId = `${date}_afternoon`;

        const morningDocRef = firebaseDoc(db, "carAvailability", morningDocId);
        const afternoonDocRef = firebaseDoc(
          db,
          "carAvailability",
          afternoonDocId
        );

        const morningDoc = await transaction.get(morningDocRef);
        const afternoonDoc = await transaction.get(afternoonDocRef);

        const carsNeeded = Math.ceil(people / 9);

        // Handle morning session
        if (!morningDoc.exists()) {
          // Create the document for the morning session with the required booking
          handleNewBooking(morningDocRef, carsNeeded, activeCars, transaction);
        } else {
          // Check availability and update the booking for the morning session
          handleExistingBooking(
            morningDoc,
            morningDocRef,
            carsNeeded,
            activeCars,
            transaction
          );
        }

        // Handle afternoon session
        if (!afternoonDoc.exists()) {
          // Create the document for the afternoon session with the required booking
          handleNewBooking(
            afternoonDocRef,
            carsNeeded,
            activeCars,
            transaction
          );
        } else {
          // Check availability and update the booking for the afternoon session
          handleExistingBooking(
            afternoonDoc,
            afternoonDocRef,
            carsNeeded,
            activeCars,
            transaction
          );
        }

        return { status: "success" };
      }

      const doc = await transaction.get(docRef);

      const carsNeeded = Math.ceil(people / 9);

      if (!date || !session) {
        throw new Error("Invalid date or session.");
      }

      if (!doc.exists()) {
        //if document does not exist, create it.
        if (carsNeeded > activeCars.length) {
          throw new Error(
            `Not enough cars for this booking. Total cars: ${activeCars.length}, Cars needed: ${carsNeeded}`
          );
        }

        const newBooking = {
          date,
          session,
        };

        let carsToLeaveAvailable = activeCars.length - carsNeeded;

        for (const car of activeCars) {
          newBooking[car] = carsToLeaveAvailable > 0;
          carsToLeaveAvailable--;
        }

        transaction.set(docRef, newBooking);
      } else {
        //if the document exists, check if there are enough available cars

        const availableCars = activeCars.filter(
          (car) => doc.data()[car]
        ).length;

        if (availableCars < carsNeeded) {
          throw new Error(
            `not enough available cars: ${availableCars} available, ${carsNeeded} cars needed`
          );
        }

        //If there are enough available cars, bookn them
        const carsToBook = activeCars
          .filter((car) => doc.data()[car])
          .slice(0, carsNeeded);

        const update = {};
        for (const car of carsToBook) {
          update[car] = false;
        }
        transaction.update(docRef, update);
      }
      return { status: "success" };
    }).catch((error) => {
      console.error("Transaction failed: ", error);
      throw error;
    });
  }
);

function handleNewBooking(docRef, carsNeeded, activeCars, transaction) {
  const [date, session] = docRef.id.split("_"); // Extract date and session from the docRef ID
  const newBooking = {
    date,
    session,
  };

  let carsToLeaveAvailable = activeCars.length - carsNeeded;

  for (const car of activeCars) {
    newBooking[car] = carsToLeaveAvailable > 0;
    carsToLeaveAvailable--;
  }

  transaction.set(docRef, newBooking);
}

function handleExistingBooking(
  doc,
  docRef,
  carsNeeded,
  activeCars,
  transaction
) {
  const availableCars = activeCars.filter((car) => doc.data()[car]).length;

  if (availableCars < carsNeeded) {
    throw new Error(
      `Not enough available cars for session: ${availableCars} available, ${carsNeeded} cars needed`
    );
  }

  const carsToBook = activeCars
    .filter((car) => doc.data()[car])
    .slice(0, carsNeeded);
  const update = {};
  for (const car of carsToBook) {
    update[car] = false;
  }
  transaction.update(docRef, update);
}

async function getActiveCarsFromDatabase() {
  try {
    const db = getFirestore();
    const carsCollection = collection(db, "cars"); // replace "cars" with your actual collection name
    const carSnapshot = await getDocs(carsCollection);
    const activeCars = [];
    carSnapshot.forEach((doc) => {
      if (doc.data().status === "active") {
        // replace "status" with your actual field name
        activeCars.push(doc.data().carName); // replace "carName" with your actual field name
      }
    });
    console.log("Active Cars:", activeCars);
    return activeCars;
  } catch (error) {
    console.error("Error fetching active cars:", error);
    throw error; // This will propagate the error up to the calling function
  }
}
